import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Output,
  EventEmitter,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormGroup, FormControl, UntypedFormControl } from '@angular/forms';
import { LocaleService } from '@next/core-lib/i18n';
import { Store, select } from '@ngrx/store';
import { keys } from 'lodash';
import { Subscription, combineLatest, of, interval } from 'rxjs';
import { startWith, filter, map, debounce } from 'rxjs/operators';
import { SiteState, Crop, TemplateType, TemplateFilter, provinces } from 'src/app/shared/models';

import { selectCropAids } from '../crop-aid/store/crop-aid.selectors';
import { selectSoilTypes } from '../soil-type/store/soil-type.selectors';
import { ClearTemplateFilter } from '../store/actions';
import { selectCrops, getTemplateFilter } from '../store/selectors';

@Component({
  selector: 'app-template-filter',
  templateUrl: './template-filter.component.html',
  standalone: false,
})
export class TemplateFilterComponent implements OnInit, OnDestroy, OnChanges {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);

  @Output() update = new EventEmitter();
  @Output() filterEnabled = new EventEmitter();
  templateFilter$ = this.store.pipe(select(getTemplateFilter));
  hasLoadedFilters = false;
  cropId = new FormControl();
  soilTypeId = new FormControl();
  isCorrupt = new UntypedFormControl();
  templateType = new FormControl(null);
  provinces = new FormControl();
  cropAidId = new FormControl();

  filterForm = new FormGroup({
    cropId: this.cropId,
    soilTypeId: this.soilTypeId,
    isCorrupt: this.isCorrupt,
    templateType: this.templateType,
    provinces: this.provinces,
    cropAidId: this.cropAidId,
  });

  previousFilterData: object;
  crops$ = this.store.pipe(select(selectCrops));
  filteredCrops$ = combineLatest([
    this.filterForm.get('cropId').valueChanges.pipe(startWith('')),
    this.crops$,
  ]).pipe(
    filter(([value, _]) => typeof value === 'string'),
    map(([value, crops]) => this.filterItems(value as string, crops)),
  );

  soils$ = this.store.pipe(select(selectSoilTypes));
  filteredSoils$ = combineLatest([
    this.filterForm.get('soilTypeId').valueChanges.pipe(startWith('')),
    this.soils$,
  ]).pipe(
    filter(([value, _]) => typeof value === 'string'),
    map(([value, soils]) => this.filterItems(value as string, soils)),
  );

  cropAidIds$ = this.store.pipe(select(selectCropAids));
  filteredCropAids$ = combineLatest([
    this.filterForm.get('cropAidId').valueChanges.pipe(startWith('')),
    this.cropAidIds$,
  ]).pipe(
    filter(([value, _]) => typeof value === 'string'),
    map(([value, cropAidIds]) => this.filterItems(value as string, cropAidIds)),
  );

  readonly provincesArray = [{ label: '', value: null }, ...provinces];

  isCorruptValues = [
    {
      value: true,
      text: this.locale.translate('yes'),
    },
    {
      value: false,
      text: this.locale.translate('no'),
    },
  ];

  templateTypes = [
    {
      value: TemplateType.GBM,
      text: this.locale.translate(TemplateType.GBM),
    },
    {
      value: TemplateType.Fertilization,
      text: this.locale.translate(TemplateType.Fertilization),
    },
  ];

  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    this.subscriptions.push(
      this.filterForm.get('cropId').valueChanges.subscribe((cropId: number) => {
        if (!cropId) {
          return;
        }
        this.subscriptions.push(
          this.crops$
            .pipe(map((crops) => crops.find((crop) => crop.id === cropId)))
            .subscribe((crop) => {
              if (!crop) {
                this.filterForm.get('cropId').setValue(null, { emitModelToViewChange: false });
              }
            }),
        );
      }),
    );

    this.previousFilterData = this.filterForm.value as object;
    this.filterForm.valueChanges
      .pipe(
        filter((_) => this.hasLoadedFilters),
        debounce(() => interval(100)),
      )
      .subscribe((filterData: TemplateFilter) => {
        if (filterData.cropId !== null && typeof filterData.cropId !== 'number') {
          filterData.cropId = null;
        }

        if (filterData.soilTypeId !== null && typeof filterData.soilTypeId !== 'number') {
          filterData.soilTypeId = null;
        }

        if (filterData.isCorrupt !== null) {
          if (typeof filterData.isCorrupt !== 'boolean') {
            filterData.isCorrupt = null;
          }
        }

        if (filterData.templateType !== null) {
          if (typeof filterData.templateType !== 'string') {
            filterData.templateType = null;
          } else {
            if (
              filterData.templateType !== TemplateType.Fertilization &&
              filterData.templateType !== TemplateType.GBM
            ) {
              filterData.templateType = null;
            }
          }
        }
        if (filterData.provinces !== null) {
          if (typeof filterData.provinces !== 'string' || filterData.provinces.length === 0) {
            filterData.provinces = null;
          }
        }

        if (filterData.cropAidId !== null && typeof filterData.cropAidId !== 'number') {
          filterData.cropAidId = null;
        }

        if (JSON.stringify(filterData) !== JSON.stringify(this.previousFilterData)) {
          this.previousFilterData = filterData;
          this.update.emit(filterData);
          this.filterEnabled.emit(
            filterData.cropId !== null ||
              filterData.soilTypeId !== null ||
              filterData.isCorrupt !== null ||
              filterData.templateType !== null ||
              filterData.provinces !== null ||
              filterData.cropAidId !== null,
          );
        }
      });

    this.subscriptions.push(
      this.templateFilter$.subscribe((templateFilter) => {
        if (!this.hasLoadedFilters || this.isClearingFilter(templateFilter)) {
          const filterNames = keys(templateFilter);
          filterNames.forEach((name) => {
            const control = this.filterForm.get(name);
            if (control) {
              control.setValue(templateFilter[name]);
            }
          });
          this.hasLoadedFilters = true;
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.filter) {
      const oldValue = this.filterForm.value;
      const newValue = {
        cropId: null,
        soilTypeId: null,
        isCorrupt: null,
        templateType: null,
        provinces: null,
        cropAidId: null,
      } as TemplateFilter;
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        this.filterForm.setValue(newValue);
      }
    }
  }

  isClearingFilter(templateFilter: TemplateFilter) {
    const filterNames = keys(templateFilter);
    let clearingFilter = true;
    filterNames.forEach((name) => {
      if (templateFilter[name]) {
        clearingFilter = false;
      }
    });
    return clearingFilter;
  }

  resetFilter() {
    this.store.dispatch(new ClearTemplateFilter());
  }

  displayIsCorrupt() {
    return of(this.isCorruptValues).pipe(
      map((isCorruptValues) => (isCorrupt?: boolean) => {
        let foundTemplateCorruptValue: { value: boolean; text: string };
        if (isCorrupt || isCorrupt === false) {
          foundTemplateCorruptValue = isCorruptValues.find(
            (isCorruptValue) => isCorruptValue.value === isCorrupt,
          );
        }
        return foundTemplateCorruptValue ? foundTemplateCorruptValue.text : '';
      }),
    );
  }

  displayTemplateType() {
    return of(this.templateTypes).pipe(
      map((templateTypes) => (selectedTemplateType?: TemplateType) => {
        let foundTemplateType: { value: TemplateType; text: string };
        if (selectedTemplateType) {
          foundTemplateType = templateTypes.find(
            (templateType) => templateType.value === selectedTemplateType,
          );
        }
        return foundTemplateType ? foundTemplateType.text : '';
      }),
    );
  }

  displayCrop$() {
    return this.crops$.pipe(
      map((crops) => (selectedCropId?: number) => {
        let foundCrop: Crop;
        if (selectedCropId) {
          foundCrop = crops.find((crop) => crop.id === selectedCropId);
        }
        return foundCrop ? foundCrop.name : '';
      }),
    );
  }

  displaySoil$() {
    return this.soils$.pipe(
      map((soils) => (selectedSoilId?: number) => {
        if (selectedSoilId === undefined) {
          return '';
        }

        if (selectedSoilId === 0) {
          return this.locale.translate('soilTypeIndependent');
        }

        const foundSoil = soils.find((soil) => soil.id === selectedSoilId);
        return foundSoil ? foundSoil.name : '';
      }),
    );
  }

  displayCropAid$() {
    return this.cropAidIds$.pipe(
      map((cropAids) => (selectedCropAidId?: number) => {
        if (selectedCropAidId === undefined) {
          return '';
        }

        const foundCropAid = cropAids.find((cropAid) => cropAid.id === selectedCropAidId);
        return foundCropAid ? foundCropAid.name : '';
      }),
    );
  }

  displayProvince(): (selectedProvince?: string) => string {
    return (selectedProvince?: string): string => {
      if (!selectedProvince) {
        return '';
      }
      return selectedProvince;
    };
  }

  private filterItems<T extends { name: string }>(value: string, items: T[]): T[] {
    if (!value) {
      value = '';
    }

    return items
      .slice()
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .filter((option) => option.name.toLowerCase().includes(value.toLowerCase()));
  }
}
